import { api as backendApi } from "@/modules/api";

type BackendPost = <TData>(
  path: string,
  options: {
    body?: unknown;
    params?: {
      path?: Record<string, string>;
    };
  }
) => Promise<{ data?: TData; error?: unknown }>;

interface ConvexAuthTokenResponse {
  token: string;
  expires_at: string;
  space_account_id: string;
}

interface SmartViewAcknowledgementResponse {
  acknowledged: boolean;
  smart_view_id: string;
}

interface SmartViewWorkItemAcknowledgementResponse extends SmartViewAcknowledgementResponse {
  work_item_id: string;
}

function requireData<TData>(response: { data?: TData; error?: unknown }, message: string) {
  if (response.data === undefined) {
    throw new Error(message);
  }

  return response.data;
}

export const createConvexAuthToken = async ({
  spaceAccountId,
}: {
  spaceAccountId: string;
}): Promise<ConvexAuthTokenResponse> => {
  const post = backendApi.post as BackendPost;
  const response = await post<ConvexAuthTokenResponse>("/v2/convex/auth-token", {
    body: {
      space_account_id: spaceAccountId,
    },
  });

  return requireData(response, "Failed to create Convex auth token");
};

export const createSmartView = async ({
  title,
  description = "",
}: {
  title: string;
  description?: string;
}): Promise<SmartViewAcknowledgementResponse> => {
  const post = backendApi.post as BackendPost;
  const response = await post<SmartViewAcknowledgementResponse>("/v2/nyc-hack-week/smart-views/", {
    body: { title, description },
  });

  return requireData(response, "Failed to create Smart View");
};

export const materializeSmartView = async ({
  smartViewId,
}: {
  smartViewId: string;
}): Promise<SmartViewAcknowledgementResponse> => {
  const post = backendApi.post as BackendPost;
  const response = await post<SmartViewAcknowledgementResponse>(
    "/v2/nyc-hack-week/smart-views/{smart_view_id}/materialize",
    {
      params: {
        path: {
          smart_view_id: smartViewId,
        },
      },
    }
  );

  return requireData(response, "Failed to materialize Smart View");
};

export const chatWithSmartView = async ({
  smartViewId,
  message,
}: {
  smartViewId: string;
  message: string;
}): Promise<SmartViewAcknowledgementResponse> => {
  const post = backendApi.post as BackendPost;
  const response = await post<SmartViewAcknowledgementResponse>(
    "/v2/nyc-hack-week/smart-views/{smart_view_id}/chat",
    {
      params: {
        path: {
          smart_view_id: smartViewId,
        },
      },
      body: { message },
    }
  );

  return requireData(response, "Failed to update Smart View");
};

export const commentOnSmartViewBlock = async ({
  smartViewId,
  blockId,
  content,
}: {
  smartViewId: string;
  blockId: string;
  content: string;
}): Promise<SmartViewWorkItemAcknowledgementResponse> => {
  const post = backendApi.post as BackendPost;
  const response = await post<SmartViewWorkItemAcknowledgementResponse>(
    "/v2/nyc-hack-week/smart-views/{smart_view_id}/block-comments",
    {
      params: {
        path: {
          smart_view_id: smartViewId,
        },
      },
      body: {
        block_id: blockId,
        content,
      },
    }
  );

  return requireData(response, "Failed to comment on Smart View block");
};

export const analyzeSmartViewBlockSourceSync = async ({
  smartViewId,
  blockId,
  blockTitle,
  summary,
  sourceNoteIds = [],
  sourceNoteTitles = [],
}: {
  smartViewId: string;
  blockId: string;
  blockTitle: string;
  summary: string;
  sourceNoteIds?: string[];
  sourceNoteTitles?: string[];
}): Promise<SmartViewWorkItemAcknowledgementResponse> => {
  const post = backendApi.post as BackendPost;
  const response = await post<SmartViewWorkItemAcknowledgementResponse>(
    "/v2/nyc-hack-week/smart-views/{smart_view_id}/source-sync",
    {
      params: {
        path: {
          smart_view_id: smartViewId,
        },
      },
      body: {
        block_id: blockId,
        block_title: blockTitle,
        summary,
        source_note_ids: sourceNoteIds,
        source_note_titles: sourceNoteTitles,
      },
    }
  );

  return requireData(response, "Failed to analyze Smart View source sync");
};
